import { AfterContentInit, Component } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ClipboardService } from 'ngx-clipboard';
import { finalize, tap } from 'rxjs/operators';

import { Totp } from '@app/models';
import { ErrorHandlerService, NotyService, MfaService } from '@app/services';

interface ModalBeforeDismiss {
  beforeDismiss?: () => boolean;
}
export type ModalResult = void;
export enum ModalStep {
  mfaType = 1,
  setup,
  setupAlt,
  confirm,
  recoveryCode
}

@Component({
  selector: 'app-add-mfa-modal',
  templateUrl: './add-mfa-modal.component.html',
  styleUrls: ['./add-mfa-modal.component.scss'],
  standalone: false
})
export class AddMfaModalComponent implements AfterContentInit, ModalBeforeDismiss {
  currentStep = ModalStep.mfaType;
  form!: FormGroup;
  isLoading = false;
  totp: Totp;
  recoveryCode: string;
  readonly ModalStep = ModalStep;
  readonly mfaOptions = [{ type: 'totp', description: 'auth.mfa.totp' }];

  constructor(
    public modal: NgbActiveModal,
    public translateService: TranslateService,
    private formBuilder: FormBuilder,
    private notyService: NotyService,
    private errorHandlerService: ErrorHandlerService,
    private clipboardService: ClipboardService,
    private mfaService: MfaService
  ) {}

  ngAfterContentInit() {
    this.form = this.formBuilder.group({ mfaType: this.mfaOptions[0].type, otpCode: '' });
  }

  beforeDismiss() {
    // Magic method called from service. Avoids dismiss on last step, uses modal.close() instead
    if (this.currentStep === ModalStep.recoveryCode) {
      this.modal.close();
      return false;
    }
    return true;
  }

  next() {
    // const mfaType = this.form.get('mfaType').value;
    const stepProgression = {
      [ModalStep.mfaType]: ModalStep.setup,
      [ModalStep.setup]: ModalStep.confirm,
      [ModalStep.setupAlt]: ModalStep.confirm,
      [ModalStep.confirm]: ModalStep.recoveryCode
    } as Record<ModalStep, ModalStep>;
    const nextStep = stepProgression[this.currentStep];

    if (!nextStep) {
      this.modal.close();
    } else {
      this.isLoading = true;

      if (this.currentStep === ModalStep.mfaType && !this.totp) {
        this.generateTOTP(nextStep);
      } else if (this.currentStep === ModalStep.confirm) {
        setTimeout(() => this.enableTOTP(nextStep));
      } else if (nextStep === ModalStep.confirm) {
        this.setNextStep(nextStep);
      } else {
        this.setNextStep(nextStep);
      }
    }
  }

  back() {
    const stepRegression = {
      [ModalStep.recoveryCode]: ModalStep.confirm,
      [ModalStep.confirm]: ModalStep.setup,
      [ModalStep.setupAlt]: ModalStep.mfaType,
      [ModalStep.setup]: ModalStep.mfaType
    } as Record<ModalStep, ModalStep>;

    if (!stepRegression[this.currentStep]) {
      this.modal.dismiss();
    } else {
      this.currentStep = stepRegression[this.currentStep] || this.currentStep;
    }
  }

  copyToClipboard(content: string) {
    this.clipboardService.copyFromContent(content);
    this.notyService.success(this.translateService.instant('notyService.copySuccess'), 2000);
  }

  private setNextStep(nextStep: ModalStep) {
    this.isLoading = false;
    this.currentStep = nextStep;
  }

  private generateTOTP(nextStep: ModalStep) {
    this.mfaService
      .generateTOTP()
      .pipe(
        tap(totp => (this.totp = totp)),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: () => (this.currentStep = nextStep),
        error: error => this.errorHandlerService.handle(error)
      });
  }

  private enableTOTP(nextStep: ModalStep) {
    const code = this.form.get('otpCode');
    code.markAsTouched();
    if (code.value.length < 6) {
      code.setErrors({ server: this.translateService.instant('error_otp_required') });
      this.isLoading = false;
      return;
    }
    this.mfaService
      .enableTOTP({ otpCode: code.value })
      .pipe(
        tap(recoveryCode => (this.recoveryCode = recoveryCode.replace(/(.{4})(?=.)/g, '$1-'))),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: () => (this.currentStep = nextStep),
        error: error => {
          code.setErrors({ server: this.translateService.instant('error_' + error.message) });
        }
      });
  }
}
